import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, X, CaretRight } from "@phosphor-icons/react";
import { useAuth } from "../lib/AuthContext";

const DISMISS_KEY = "firewatch:reporter_cta_dismissed";
const DISMISS_TTL_DAYS = 30;

function dismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    return ts && Date.now() - ts < DISMISS_TTL_DAYS * 86400 * 1000;
  } catch {
    return false;
  }
}

export default function VerifiedReporterCTA() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(dismissedRecently());

  // Hide for already-verified users, for users with a pending application,
  // and for users who dismissed.
  if (dismissed) return null;
  if (user?.verified) return null;
  if (user?.reporter_application) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  const target = user ? "/apply-reporter" : "/signup?next=/apply-reporter";

  return (
    <section
      data-testid="reporter-cta"
      className="border-b border-tac-border bg-tac-surface/40 relative group"
    >
      <button
        data-testid="reporter-cta-dismiss"
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-tac-faint hover:text-tac-text p-0.5 opacity-60 group-hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <X size={11} weight="bold" />
      </button>
      <Link
        to={target}
        className="block px-4 py-3 pr-8 hover:bg-tac-surface/70 transition-colors"
        data-testid="reporter-cta-link"
      >
        <div className="flex items-center gap-2 mb-0.5">
          <ShieldCheck size={14} weight="duotone" className="text-tac-primary" />
          <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-tac-primary">
            Become a Verified Reporter
          </span>
        </div>
        <p className="text-[13px] text-tac-text leading-snug mb-1.5">
          Live local? Have ties to fire ops, EM, scanner monitoring, or ham
          radio? Your on-scene updates carry a gold shield trusted by the
          community.
        </p>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-tac-primary group-hover:underline">
          Apply in 3 minutes <CaretRight size={10} weight="bold" />
        </span>
      </Link>
    </section>
  );
}
